import { useState } from 'react';

import { useStatic } from '~/common/hooks/useStatic';
import { useEventHandler } from '../useEventHandler';

/**
 * A custom hook to manage state that can operate in both controlled and uncontrolled modes.
 *
 * @template T The type of the state value.
 *
 * @param {Object} options - Configuration object for the hook.
 * @param {T} [options.value] - The controlled state value. If provided, the hook operates in controlled mode.
 * @param {(value: T) => void} [options.onChange] - Callback function to handle updates to the controlled state. Required if `value` is provided.
 * @param {T} options.initialInternalValue - The initial value for the internal state. Used when the hook operates in uncontrolled mode.
 *
 * @returns {readonly [T, (next: T) => void, boolean]} A tuple containing:
 * - The current state value.
 * - A function to update the state value.
 * - A boolean indicating if the hook is operating in controlled mode (`true` if controlled, `false` otherwise).
 *
 * @throws {Error} During render, when `onChange` goes from defined to undefined or
 * undefined to defined after the first render, since controlled-ness is fixed for
 * the life of the component.
 */
export const useControllableState = <T>({
  value,
  onChange,
  initialInternalValue,
}: {
  value: T | undefined;
  onChange: ((value: T) => void) | undefined;
  initialInternalValue: T;
}): readonly [T, (next: T) => void, boolean] => {
  const isControlled = useStatic(onChange !== undefined);

  const [internal, setInternal] = useState<T>(initialInternalValue);

  const state = (isControlled ? value : internal) as T;

  const setState = useEventHandler((next: T) => {
    if (onChange) {
      onChange(next);
    } else {
      setInternal(next);
    }
  });

  // checked after every hook call so the hook order of the throwing render
  // still matches the previous one
  if (isControlled !== (onChange !== undefined)) {
    throw new Error(
      isControlled
        ? 'useControllableState: onChange was removed after the first render. A component cannot change from controlled to uncontrolled; pass an onChange for the whole life of the component.'
        : 'useControllableState: onChange was added after the first render. A component cannot change from uncontrolled to controlled; pass an onChange from the first render or never pass one.',
    );
  }

  return [state, setState, isControlled] as const;
};
